// Which platform an insight source came from.
//
// Extracted from InsightDetailModal so the rules can be exercised directly:
// they are pure string work, and the interesting cases are URLs rather than
// rendering.

// Matched against the URL's hostname, anchored to domain-label boundaries so a
// domain can only match as a real (sub)domain — never as an incidental
// substring. Without anchoring, e.g. `t.co` (Twitter) matches "blogspo(t.co)m"
// and a blogspot article gets mislabelled "Twitter".
const HOST_PATTERNS: [RegExp, string][] = [
  [/(?:^|\.)(facebook\.com|fb\.com|fb\.watch|fbwat\.ch)$/i, 'facebook'],
  [/(?:^|\.)(instagram\.com|instagr\.am)$/i, 'instagram'],
  [/(?:^|\.)tiktok\.com$/i, 'tiktok'],
  [/(?:^|\.)(youtube\.com|youtu\.be)$/i, 'youtube'],
  [/(?:^|\.)(linkedin\.com|lnkd\.in)$/i, 'linkedin'],
  [/(?:^|\.)(twitter\.com|x\.com|t\.co)$/i, 'twitter'],
  [/(?:^|\.)(reddit\.com|redd\.it)$/i, 'reddit'],
  [/(?:^|\.)threads\.net$/i, 'threads'],
  [/(?:^|\.)tripadvisor\.[a-z.]+$/i, 'tripadvisor'],
  [/(?:^|\.)booking\.com$/i, 'booking'],
]

// Every Meta business-inbox conversation is linked under facebook.com no
// matter which channel it arrived on, so the host says nothing about the
// channel — an Instagram DM and a Messenger thread share it. The channel is in
// the path (/inbox/instagram_direct) and the thread_type query parameter.
const META_INBOX_PATH = /\/(?:latest\/)?inbox\//i
const INBOX_CHANNELS: [RegExp, string][] = [
  [/instagram|ig_message|ig_direct/i, 'instagram'],
  [/whatsapp|wa_message/i, 'whatsapp'],
  [/messenger|fb_message|page_message/i, 'facebook'],
]

/**
 * The platform a link points at, or null when the URL does not identify one —
 * in which case the caller keeps whatever the source already claims.
 */
export const detectPlatformFromUrl = (url: string): string | null => {
  if (!url) return null
  let host: string
  let path: string
  let search: string
  try {
    const parsed = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`)
    host = parsed.hostname.toLowerCase()
    path = parsed.pathname.toLowerCase()
    search = parsed.search.toLowerCase()
  } catch {
    // Relative or unparseable URL — let the caller fall back to the stored
    // platform.
    return null
  }

  // A Meta inbox link, before the host patterns get to call everything
  // Facebook. Returns null rather than guessing when the channel is not in the
  // URL, so a correct stored platform survives.
  if (META_INBOX_PATH.test(path)) {
    const haystack = `${path}${search}`
    for (const [pattern, platform] of INBOX_CHANNELS) {
      if (pattern.test(haystack)) return platform
    }
    return null
  }

  // Google Maps reviews are distinguished by the /maps path, not the host.
  if (
    /(?:^|\.)(google\.[a-z.]+|maps\.google\.[a-z.]+)$/i.test(host) &&
    path.startsWith('/maps')
  ) {
    return 'google reviews'
  }
  for (const [pattern, platform] of HOST_PATTERNS) {
    if (pattern.test(host)) return platform
  }
  return null
}

interface PlatformSource {
  links?: { url: string }[]
  platform?: string
}

// The stored `source.platform` is generator-provided and often generic/wrong
// (e.g. a LinkedIn or Facebook post tagged "news & blogs"). The link URL is
// the ground truth, so prefer URL detection and only fall back to the stored
// platform when the URL identifies nothing — a real news site, or a Meta inbox
// link whose channel is not in the address.
export const resolveSourcePlatform = (source: PlatformSource): string => {
  const fromUrl = source.links
    ?.map((l) => detectPlatformFromUrl(l.url))
    .find(Boolean)
  return fromUrl || source.platform || ''
}
